import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text/text';
import {formatTextWithMoment} from '@Utils/friendly-moment.util';
import {EntityDraftCard} from '@Components/ai-chat-window/components/entity-draft-card/entity-draft-card';
import type {EntityDraftGroupProps, EntityDraftLeftOutItem} from './entity-draft-group.types';
import {DraftApproveButton} from './components/draft-approve-button/draft-approve-button';
import styles from './entity-draft-group.module.scss';

export function EntityDraftGroup({title, note, noteMoment, cards, leftOut = [], approveLabel, approveOptions, messageAction}: EntityDraftGroupProps): ReactElement {
  const renderTitle = (): ReactElement | null => {
    if (title === undefined || title === '') {
      return null;
    }

    return <Text val={title} size={TextSize.LG} bold className={styles.draftGroupTitle} />;
  };

  const renderNote = (): ReactElement | null => {
    if (note === undefined || note === '') {
      return null;
    }

    return <Text val={formatTextWithMoment(note, noteMoment)} size={TextSize.MD} className={styles.draftGroupNote} />;
  };

  const renderLeftOutRow = (item: EntityDraftLeftOutItem, index: number): ReactElement => {
    return (
      <li key={`${item.label}-${index}`} className={styles.leftOutRow}>
        <Text val={item.label} size={TextSize.MD} bold className={styles.leftOutLabel} />
        <Text val={item.reason} size={TextSize.MD} className={styles.leftOutReason} />
      </li>
    );
  };

  const renderLeftOut = (): ReactElement | null => {
    if (leftOut.length === 0) {
      return null;
    }

    const leftOutHeading = window.i18next.t('pmwjs_ai_chat_draft_group_left_out');

    return (
      <div className={styles.leftOutSection}>
        <Text val={leftOutHeading} size={TextSize.SM} bold className={styles.leftOutHeading} />
        <ul className={styles.leftOutList} role="list" aria-label={leftOutHeading}>
          {leftOut.map(renderLeftOutRow)}
        </ul>
      </div>
    );
  };

  const renderApproveButton = (): ReactElement | null => {
    if (approveLabel === undefined || approveLabel === '') {
      return null;
    }

    return <DraftApproveButton label={approveLabel} approveOptions={approveOptions} messageAction={messageAction} />;
  };

  return (
    <div className={styles.entityDraftGroup}>
      {renderTitle()}
      {renderNote()}
      <ul className={styles.draftCardList} role="list" aria-label={window.i18next.t('pmwjs_ai_chat_draft_group_drafts')}>
        {cards.map((card, index) => (
          <li key={`${card.meta.join('|')}-${index}`} className={styles.draftCardItem}>
            <EntityDraftCard {...card} />
          </li>
        ))}
      </ul>
      {renderLeftOut()}
      {renderApproveButton()}
    </div>
  );
}
